import { useEffect, useRef, useState } from "react";
import TimeField from "src/Common/TimeField";

import ConfirmDialog from "src/Common/confirmdialog";

import {
  describeEntry,
  durationBetween,
  emptySpans,
  entryHasTime,
  labelEntrySpan,
  labelZeroSpan,
  overlapConflicts,
} from "src/Common/hours";

/**
 * One day's stretches, editable: numbered cards, add and remove with the
 * two-tier confirmation, a refusal to save hours that overlap or record no
 * time, and a day that can be cleared. Used by the work order timesheet and by
 * Time Management, so the two cannot drift — the judgements come from
 * common/hours and the interaction lives here, once.
 *
 * The parent owns loading and saving. This component owns the draft: it takes
 * the stored entries, hands back the entries to save (already validated) and,
 * when asked for one, the reason the parent must have before it writes.
 */

const blankEntry = () => ({
  // Marks a stretch that exists only on this screen. Removing one costs
  // nothing, so it is not guarded the way removing saved hours is.
  isUnsaved: true,
  regularStart: null,
  regularStop: null,
  travelStart: null,
  travelStop: null,
  regularTime: null,
  travelTime: null,
  overTime: null,
});

const fromStored = (entry) => ({
  isUnsaved: false,
  regularStart: entry.regularStart ?? null,
  regularStop: entry.regularStop ?? null,
  travelStart: entry.travelStart ?? null,
  travelStop: entry.travelStop ?? null,
  regularTime: entry.regularTime ?? null,
  travelTime: entry.travelTime ?? null,
  // Carried, never edited here: overtime is derived, and dropping it would
  // wipe a figure already stored on the day.
  overTime: entry.overTime ?? null,
});

// What a stretch is worth: the span when both ends are present, otherwise the
// duration stored before the screens captured clock times.
export const entryRegular = (entry) =>
  durationBetween(entry?.regularStart, entry?.regularStop) ?? entry?.regularTime;
export const entryTravel = (entry) =>
  durationBetween(entry?.travelStart, entry?.travelStop) ?? entry?.travelTime;

/** The draft as the timesheet stores it: only the fields that carry a value. */
const toStored = (entry) => {
  const data = {};
  const regular = entryRegular(entry);
  const travel = entryTravel(entry);
  if (regular) data.regularTime = regular;
  if (travel) data.travelTime = travel;
  if (entry.overTime) data.overTime = entry.overTime;
  if (entry.regularStart) data.regularStart = entry.regularStart;
  if (entry.regularStop) data.regularStop = entry.regularStop;
  if (entry.travelStart) data.travelStart = entry.travelStart;
  if (entry.travelStop) data.travelStop = entry.travelStop;
  return data;
};

const DayEntriesEditor = ({
  initialEntries,
  // Identifies the day being edited. The draft resets when this changes and
  // only then: parents build `initialEntries` fresh each render, and resetting
  // on the array itself wiped the typed times the moment the parent re-rendered
  // — which it does on Save, as `saving` flips.
  resetKey,
  onSave,
  onCancel,
  saving = false,
  requireReason = false,
  reasonPrompt = "Why is this being changed?",
  className = "",
}) => {
  const [draft, setDraft] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonMissing, setReasonMissing] = useState(false);
  // The confirmation currently on screen, if any: the app's own centred dialog
  // rather than the browser's window.confirm, which renders as a bare
  // "localhost:3000 says" box in the corner.
  const [dialog, setDialog] = useState(null);
  const latestInitial = useRef(initialEntries);
  latestInitial.current = initialEntries;

  useEffect(() => {
    const stored = (latestInitial.current || []).map(fromStored);
    setDraft(stored.length ? stored : [blankEntry()]);
    setIsDirty(false);
    setReason("");
    setReasonMissing(false);
  }, [resetKey]);

  const setField = (index, field, value) => {
    setIsDirty(true);
    setDraft((previous) =>
      previous.map((entry, position) =>
        position === index ? { ...entry, [field]: value || null } : entry
      )
    );
  };

  const addEntry = () => {
    setIsDirty(true);
    setDraft((previous) => [...previous, blankEntry()]);
  };

  // Allowed to empty. A day whose only stretch was recorded in error has to be
  // clearable, and refusing the last removal made that impossible.
  const dropEntry = (index) => {
    setIsDirty(true);
    setDraft((previous) => previous.filter((_entry, position) => position !== index));
  };

  /**
   * Two tiers. A blank row costs one click; hours that were already saved name
   * themselves and say there is no undo. Clicking Remove is the first step and
   * the confirm is the second.
   */
  const removeEntry = (index) => {
    const entry = draft[index];
    if (!entry) return;
    if (!entryHasTime(entry)) {
      dropEntry(index);
      return;
    }
    const summary = describeEntry(entry);
    setDialog(
      entry.isUnsaved
        ? {
            title: "Remove this entry?",
            description: "The times entered on it will be discarded.",
            onConfirm: () => dropEntry(index),
          }
        : {
            title: "Remove saved hours?",
            description: `Entry ${index + 1} records ${summary || "no time"}. It will be removed when you save, and that cannot be undone.`,
            onConfirm: () => dropEntry(index),
          }
    );
  };



  const conflicts = overlapConflicts(draft, labelEntrySpan);
  const empties = emptySpans(draft, labelZeroSpan);
  const flagged = new Set([
    ...conflicts.flatMap(([a, b]) => [a.index, b.index]),
    ...empties.map((span) => span.index),
  ]);

  // Whether the day as it would be saved differs from the day as it was
  // loaded. Compared in stored form rather than by "was anything typed", so a
  // time changed and changed back is not a change — and a reason is only asked
  // for when there is a change to give one for.
  const asSaved = (entries) => JSON.stringify(entries.map(toStored).filter(entryHasTime));
  const hasChanges = asSaved(draft) !== asSaved((initialEntries || []).map(fromStored));
  const askReason = requireReason && hasChanges;

  const cancel = () => {
    if (isDirty && hasChanges) {
      setDialog({
        title: "Discard changes?",
        description: "The hours you changed here have not been saved and will be lost.",
        onConfirm: onCancel,
      });
      return;
    }
    onCancel();
  };

  const save = () => {
    // Refused rather than trimmed: two stretches covering the same clock time
    // pay the hour twice, and a stretch that starts and stops at the same
    // moment records nothing. Only the person who worked it knows which is
    // wrong.
    // Refused, and the red panel above the buttons already says exactly why —
    // it names every clash and every empty stretch — so no dialog repeats it.
    if (conflicts.length || empties.length) return;
    // Nothing changed: there is nothing to write and nothing to explain, so
    // Save just closes rather than writing an identical day (and a ledger line
    // saying nothing moved).
    if (!hasChanges) {
      onCancel();
      return;
    }
    const trimmedReason = reason.trim();
    if (askReason && !trimmedReason) {
      setReasonMissing(true);
      return;
    }
    onSave(draft.map(toStored).filter(entryHasTime), trimmedReason || null);
  };

  return (
    <div className={`w-full bg-[#F5F8FF] px-4 py-3 ${className}`}>
      {draft.map((entry, index) => (
        <div
          key={index}
          className={`w-full rounded-[8px] border-[1px] border-l-[4px] bg-white px-3 pt-2 pb-1 ${
            index > 0 ? "mt-3" : ""
          } ${flagged.has(index) ? "border-[#FCA5A5] border-l-[#DC2626]" : "border-borderColor border-l-[#0065FD]"}`}
        >
          {/* Remove is offered on a lone entry as well, so the day can be
              cleared. The ENTRY number waits until there are two: numbering a
              single entry invites the question of where the second one is. */}
          <div className="w-full flex items-center justify-between mb-2 pb-1 border-b-[1px] border-borderColor">
            <p
              className={`font-OpenSansSemiBold text-[12px] ${
                flagged.has(index) ? "text-[#DC2626]" : "text-[#0065FD]"
              }`}
            >
              {draft.length > 1 ? `ENTRY ${index + 1}` : ""}
            </p>
            <button
              type="button"
              onClick={() => removeEntry(index)}
              className="font-OpenSansRegular text-[12px] text-[#92400E] underline"
            >
              Remove
            </button>
          </div>

          <SpanRow
            label="WORK HOURS"
            start={entry.regularStart}
            stop={entry.regularStop}
            total={entryRegular(entry)}
            onStart={(value) => setField(index, "regularStart", value)}
            onStop={(value) => setField(index, "regularStop", value)}
          />
          <SpanRow
            label="TRAVEL TIME"
            start={entry.travelStart}
            stop={entry.travelStop}
            total={entryTravel(entry)}
            onStart={(value) => setField(index, "travelStart", value)}
            onStop={(value) => setField(index, "travelStop", value)}
          />
          {/* No overtime box: it is derived from the stretches above, here as
              everywhere else. Anything already stored is carried through. */}
        </div>
      ))}

      {draft.length === 0 && (
        <div className="w-full rounded-[8px] border-[1px] border-borderColor bg-white px-3 py-2">
          <p className="font-OpenSansRegular text-[13px] text-subTitleColor">
            No hours on this day. Saving now removes what was recorded.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={addEntry}
        className="font-OpenSansSemiBold text-[13px] text-[#0065FD] mt-3"
      >
        {draft.length === 0 ? "+ Add an entry" : "+ Add another entry"}
      </button>

      {(conflicts.length > 0 || empties.length > 0) && (
        <div className="w-full rounded-[8px] border-[1px] border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 mt-3">
          <p className="font-OpenSansSemiBold text-[12px] text-[#B91C1C] mb-[2px]">
            {conflicts.length > 0 ? "Overlapping hours" : "Entry records no time"}
          </p>
          {conflicts.map(([a, b], position) => (
            <p key={`o-${position}`} className="font-OpenSansRegular text-[12px] text-[#B91C1C]">
              {a.describe} overlaps {b.describe}.
            </p>
          ))}
          {empties.map((span, position) => (
            <p key={`e-${position}`} className="font-OpenSansRegular text-[12px] text-[#B91C1C]">
              {span.describe}, so it records no time.
            </p>
          ))}
        </div>
      )}

      {askReason && (
        // On its own amber panel: the reason is the one thing this save cannot
        // go without, and a bare box under the time fields was easy to scroll
        // past. Same amber as the lunch bands, which already mean "payroll
        // should read this".
        <div className="w-full max-w-[560px] mt-3 rounded-[10px] border border-[#FCD34D] bg-[#FFFBEB] px-3 py-2.5">
          <p className="font-OpenSansSemiBold text-[12px] text-[#92400E] mb-1.5 flex items-center gap-2">
            REASON
            <span className="px-1.5 py-[1px] rounded-[4px] bg-[#FDE68A] text-[#78350F] text-[10px] leading-4 uppercase tracking-[0.5px]">
              required
            </span>
          </p>
          <textarea
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              if (event.target.value.trim()) setReasonMissing(false);
            }}
            placeholder={reasonPrompt}
            rows={2}
            className={`w-full rounded-[8px] border-[1px] bg-white px-3 py-2 font-OpenSansRegular text-[13px] text-titleColor outline-none resize-y placeholder:text-[#A0A6BA] focus:border-[#0065FD] focus:ring-2 focus:ring-[#DCE8FF] ${
              reasonMissing ? "border-[#DC2626] ring-2 ring-[#FEE2E2]" : "border-[#D6A94A]"
            }`}
          />
          {reasonMissing && (
            <p className="font-OpenSansRegular text-[12px] text-[#B91C1C] mt-1">
              A reason is required for changes made from here. It is kept with the change in the
              hours history.
            </p>
          )}
        </div>
      )}

      <ConfirmDialog
        shown={Boolean(dialog)}
        title={dialog?.title}
        description={dialog?.description}
        onConfirmClicked={() => {
          const action = dialog?.onConfirm;
          setDialog(null);
          if (action) action();
        }}
        onCancelClicked={() => setDialog(null)}
      />

      <div className="w-full flex items-center gap-3 mt-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className={`h-[32px] px-4 rounded-[8px] font-OpenSansSemiBold text-[13px] text-white ${
            saving ? "bg-[#93C5FD]" : "bg-[#0065FD]"
          }`}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={cancel}
          className="h-[32px] px-4 rounded-[8px] border-[1px] border-borderColor font-OpenSansSemiBold text-[13px] text-subTitleColor bg-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DayEntriesEditor;

/**
 * One labelled start/stop pair and the duration it works out to. The fields
 * hand back "HH:mm", the shape the timesheet stores, so there is nothing to
 * parse here and nothing to get wrong.
 */
const SpanRow = ({ label, start, stop, total, onStart, onStop }) => {
  // The row label is set in capitals; the panel title reads better in sentence
  // case: "Work hours · Start".
  const spoken = label ? label.charAt(0) + label.slice(1).toLowerCase() : "";
  return (
  <div className="w-full flex items-center gap-3 mb-2">
    <p className="font-OpenSansSemiBold text-[12px] text-subTitleColor w-[110px]">{label}</p>
    <TimeField
      value={start ?? null}
      onChange={(next) => onStart(next ?? "")}
      label={spoken}
      end="Start"
      placeholder="Start"
    />
    <span className="font-OpenSansRegular text-[13px] text-subTitleColor">to</span>
    <TimeField
      value={stop ?? null}
      onChange={(next) => onStop(next ?? "")}
      label={spoken}
      end="Stop"
      placeholder="Stop"
    />
    <span className="font-OpenSansSemiBold text-[14px] text-titleColor ml-2">{total || "00:00"}</span>
  </div>
  );
};
